import { AiMessageSteps } from "__generated__/graphql"

import { asRecord, parseJsonIfPossible } from "./aiStepsDrawer.utils"

/**
 * A v2 run read the way it happened: prepare, then N agent rounds each ending in a tool call or
 * the output, then the delivery decision.
 *
 * The raw step list cannot be read that way — one turn is 25 rows where AGENT_MODEL_CALL and
 * AGENT_TOOL_CALL alternate, and the decisions that matter (which role, which tools were on the
 * table, what the agent chose, what actually went out) sit in four different rows' payloads.
 */

export type V2ToolCall = {
  stepId: string
  /** Round it happened in, 1-based. */
  round: number
  tool: string
  ok: boolean
  durationMs: number
  summary: string
  /** The one argument worth seeing at a glance — a query, a reference id, a product id. */
  argument: string
}

export type V2Round = {
  round: number
  modelMs: number
  tools: V2ToolCall[]
}

export type V2RunOverview = {
  role: string | null
  roleReason: string | null
  surface: string | null
  /** What the host put on the table before the agent ran. */
  offered: {
    knowledge: boolean
    guidance: number
    skills: string[]
    activeSkill: string | null
    actions: string[]
    commerceLane: string | null
    commerceTools: string[]
  }
  rounds: V2Round[]
  /** What the agent decided. */
  output: {
    action: string | null
    confidence: string | null
    language: string | null
    answeringRole: string | null
    conversationState: string | null
    handoffReason: string | null
    knowledgeGap: string | null
    notes: string | null
  } | null
  /** What the host did with that decision. */
  delivery: {
    code: string | null
    sent: boolean
    dm: boolean
    productCards: number
    actions: string[]
  } | null
  modelCalls: number
  toolCalls: number
  failures: number
}

const asText = (value: unknown): string =>
  typeof value === "string" ? value.trim() : ""

const asCount = (value: unknown): number =>
  typeof value === "number" && Number.isFinite(value) ? value : 0

const asList = (value: unknown): string[] =>
  Array.isArray(value) ? value.map(asText).filter(Boolean) : []

const stepName = (step: AiMessageSteps): string => String(step.step ?? "")

/** The single argument worth a glance; the rest stays in the step's own JSON. */
function pickArgument(args: Record<string, unknown> | null): string {
  if (!args) return ""
  for (const key of [
    "query",
    "referenceId",
    "productId",
    "skillId",
    "stepId",
    "search",
    "orderId"
  ]) {
    const value = asText(args[key])
    if (value) return value
  }
  return ""
}

export function isV2Run(steps: AiMessageSteps[] | undefined | null): boolean {
  return (steps ?? []).some((step) =>
    ["AGENT_CONTEXT", "AGENT_OUTPUT", "DELIVERY_GATE"].includes(stepName(step))
  )
}

export function buildV2RunOverview(
  steps: AiMessageSteps[] | undefined | null
): V2RunOverview | null {
  const list = steps ?? []
  if (!isV2Run(list)) return null

  const find = (name: string) => list.find((step) => stepName(step) === name)
  const request = (step: AiMessageSteps | undefined) =>
    asRecord(parseJsonIfPossible(step?.request))
  const response = (step: AiMessageSteps | undefined) =>
    asRecord(parseJsonIfPossible(step?.response))

  const roleStep = find("ROLE_SELECTION")
  const roleResponse = response(roleStep)
  const context = request(find("AGENT_CONTEXT"))
  const commerce = asRecord(context?.commerce)
  const skills = asRecord(context?.skills)
  const guidance = asRecord(context?.guidance)
  const outputResponse = response(find("AGENT_OUTPUT"))
  const agentOutput = asRecord(outputResponse?.output)
  const gate = response(find("DELIVERY_GATE"))

  const rounds: V2Round[] = []
  let round = 0
  let modelCalls = 0
  let toolCalls = 0
  let failures = 0

  for (const step of list) {
    const name = stepName(step)
    if (name === "AGENT_MODEL_CALL") {
      round += 1
      modelCalls += 1
      rounds.push({ round, modelMs: step.durationMs ?? 0, tools: [] })
      continue
    }
    if (name !== "AGENT_TOOL_CALL") continue

    toolCalls += 1
    const failed = String(step.status ?? "") === "FAILED"
    if (failed) failures += 1
    const call: V2ToolCall = {
      stepId: String(step._id),
      round: Math.max(round, 1),
      tool: asText(request(step)?.tool) || "tool",
      ok: !failed,
      durationMs: step.durationMs ?? 0,
      summary: asText(step.summary),
      argument: pickArgument(asRecord(request(step)?.arguments))
    }
    const current = rounds[rounds.length - 1]
    if (current) current.tools.push(call)
    else rounds.push({ round: 1, modelMs: 0, tools: [call] })
  }

  return {
    role: asText(roleResponse?.role) || null,
    roleReason: asText(roleResponse?.reason) || null,
    surface: asText(context?.surface) || null,
    offered: {
      knowledge: context?.knowledgeAvailable === true,
      guidance: asCount(guidance?.total),
      skills: asList(skills?.candidates),
      activeSkill: asText(skills?.active) || null,
      actions: asList(context?.channelActions),
      commerceLane: asText(commerce?.lane) || null,
      commerceTools: asList(commerce?.tools)
    },
    rounds,
    output: agentOutput
      ? {
          action: asText(agentOutput.action) || null,
          confidence: asText(agentOutput.confidence) || null,
          language: asText(agentOutput.language) || null,
          answeringRole: asText(agentOutput.answeringRole) || null,
          conversationState: asText(agentOutput.conversationState) || null,
          handoffReason: asText(agentOutput.handoffReason) || null,
          knowledgeGap: asText(agentOutput.knowledgeGap) || null,
          notes: asText(agentOutput.notes) || null
        }
      : null,
    delivery: gate
      ? {
          code: asText(gate.code) || null,
          sent: gate.sent === true,
          dm: gate.dm === true,
          productCards: asCount(gate.productCards),
          actions: asList(gate.actions)
        }
      : null,
    modelCalls,
    toolCalls,
    failures
  }
}
